import { IntegrationAppClient, parseDataLocationPath } from '@integration-app/sdk';
export * from '@integration-app/sdk';
export { DataForm } from '@integration-app/sdk';
import { jsx } from 'react/jsx-runtime';
import { createContext, useMemo, useContext, useState } from 'react';
import useSWR from 'swr';
import AwesomeDebouncePromiseImport from 'awesome-debounce-promise';
import useSWRInfinite from 'swr/infinite';
import qs from 'query-string';

const IntegrationAppContext = createContext(null);
IntegrationAppContext.displayName = 'IntegrationAppClientContext';
const IntegrationAppProvider = ({ token, fetchToken, credentials, fetchCredentials, apiUri = null, uiUri = null, children, }) => {
    const client = useMemo(() => new IntegrationAppClient({
        token,
        fetchToken,
        credentials,
        fetchCredentials,
        apiUri,
        uiUri,
    }), [token, JSON.stringify(credentials), apiUri, uiUri]);
    return (jsx(IntegrationAppContext.Provider, { value: client, children: children }));
};
function useIntegrationApp() {
    return useContext(IntegrationAppContext);
}

function useIntegrationAppSWR(path, options) {
    const client = useIntegrationApp();
    const fetcher = async () => {
        const response = await client.get(path, options);
        return response;
    };
    return useSWR(client ? path : undefined, fetcher, options);
}

var _a;
const AwesomeDebouncePromise = (_a = AwesomeDebouncePromiseImport === null || AwesomeDebouncePromiseImport === void 0 ? void 0 : AwesomeDebouncePromiseImport.default) !== null && _a !== void 0 ? _a : AwesomeDebouncePromiseImport;

const elementStateCache = new Map();
function useElement(selector, accessorGenerator) {
    var _a;
    const integrationApp = useIntegrationApp();
    const elementKeyData = {
        token: integrationApp === null || integrationApp === void 0 ? void 0 : integrationApp.token,
        selector,
    };
    const elementKey = JSON.stringify(elementKeyData);
    const elementState = (_a = elementStateCache.get(elementKey)) !== null && _a !== void 0 ? _a : {
        updatedLocally: false,
        savingToServer: false,
        currentPutRequests: [],
        debouncedPut: AwesomeDebouncePromise(async (data) => {
            const requestId = Math.random();
            elementState.currentPutRequests.push(requestId);
            elementState.updatedLocally = false;
            elementState.savingToServer = true;
            try {
                const result = await (accessor === null || accessor === void 0 ? void 0 : accessor.put(data));
                elementState.currentPutRequests =
                    elementState.currentPutRequests.filter((id) => id !== requestId);
                if (!elementState.updatedLocally &&
                    elementState.currentPutRequests.length === 0) {
                    elementState.savingToServer = false;
                    await mutate(result, false);
                }
            }
            catch (e) {
                elementState.updatedLocally = true;
                throw e;
            }
            finally {
                elementState.savingToServer = false;
            }
        }, 500),
    };
    if (!elementStateCache.has(elementKey)) {
        elementStateCache.set(elementKey, elementState);
    }
    const accessor = integrationApp
        ? accessorGenerator(integrationApp)
        : undefined;
    const swrKey = accessor && selector ? `element:${elementKey}` : undefined;
    const { data: item, mutate, error, isLoading, isValidating, } = useSWR(swrKey, () => accessor === null || accessor === void 0 ? void 0 : accessor.get(), {
        isPaused: () => elementState.updatedLocally || elementState.savingToServer,
    });
    const loading = isLoading;
    const refreshing = isValidating;
    async function refresh() {
        return await mutate();
    }
    async function put(data) {
        if (!(accessor === null || accessor === void 0 ? void 0 : accessor.put)) {
            throw new Error(`"put method is not supported for accessor ${accessor.constructor.name}`);
        }
        elementState.updatedLocally = true;
        const newLocalData = {
            ...item,
            ...data,
        };
        await mutate(newLocalData, false);
        await elementState.debouncedPut(data);
    }
    async function patch(data) {
        const newData = {
            ...item,
            ...data,
        };
        return put(newData);
    }
    async function archive() {
        if (!(accessor === null || accessor === void 0 ? void 0 : accessor.archive)) {
            return;
        }
        await mutate({ ...item, archivedAt: new Date().toISOString() }, false);
        await (accessor === null || accessor === void 0 ? void 0 : accessor.archive());
        await mutate();
    }
    async function create(data) {
        if (!(accessor === null || accessor === void 0 ? void 0 : accessor.create)) {
            throw new Error(`"create method is not supported for accessor ${accessor === null || accessor === void 0 ? void 0 : accessor.constructor.name}`);
        }
        const result = await (accessor === null || accessor === void 0 ? void 0 : accessor.create(data));
        return await mutate(result);
    }
    return {
        accessor,
        item,
        loading,
        saving: elementState.updatedLocally || elementState.savingToServer,
        error,
        refresh,
        refreshing,
        create,
        patch,
        put,
        archive,
    };
}

function useConnection(id) {
    const { item: connection, ...rest } = useElement(id, (integrationApp) => (id ? integrationApp.connection(id) : undefined));
    return {
        connection,
        ...rest,
    };
}

function useElements(route, query = {}) {
    var _a;
    const integrationApp = useIntegrationApp();
    const limit = (_a = query.limit) !== null && _a !== void 0 ? _a : 25;
    const tokenHash = hashCode(integrationApp.token);
    function getKey(page, previousPageData) {
        var _a;
        if (page === 0)
            return `/${route}?${qs.stringify({
                ...query,
                limit,
                hash: tokenHash,
            })}`;
        if (((_a = previousPageData.items) === null || _a === void 0 ? void 0 : _a.length) < limit)
            return null;
        return `/${route}?${qs.stringify({
            ...query,
            limit,
            cursor: previousPageData.cursor,
            hash: tokenHash,
        })}`;
    }
    const [loadingMore, setIsLoadingMore] = useState(false);
    const { data, size, setSize, isLoading, error, mutate, isValidating } = useSWRInfinite(getKey, (url) => integrationApp.get(url));
    const items = data ? data.map((page) => page.items).flat() : [];
    const loading = isLoading;
    const refreshing = isValidating;
    async function loadMore() {
        var _a, _b;
        const hasMoreToLoad = ((_b = (_a = data[size - 1]) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.length) === limit;
        if (hasMoreToLoad) {
            setIsLoadingMore(true);
            await setSize(size + 1);
            setIsLoadingMore(false);
        }
    }
    async function refresh() {
        await mutate();
    }
    return {
        items,
        refresh,
        refreshing,
        loadMore,
        loadingMore,
        loading,
        error,
    };
}
function hashCode(s) {
    if (!s) {
        return 0;
    }
    const l = s.length;
    let h = 0, i = 0;
    if (l > 0)
        while (i < l)
            h = ((h << 5) - h + s.charCodeAt(i++)) | 0;
    return h;
}

function useConnections(query) {
    const { ...rest } = useElements('connections', query);
    return {
        connections: rest.items,
        ...rest,
    };
}

function useConnectorSpec(integrationIdOrKey) {
    const integrationApp = useIntegrationApp();
    const { data, isLoading, error } = useSWR(integrationIdOrKey
        ? `/integrations/${integrationIdOrKey}/connector-spec`
        : undefined, () => integrationApp.integration(integrationIdOrKey).getConnectorSpec());
    return { data, loading: isLoading, error };
}

function useIntegration(idOrKey) {
    const { item: integration, ...rest } = useElement(idOrKey, (integrationApp) => idOrKey ? integrationApp.integration(idOrKey) : undefined);
    return { integration, ...rest };
}

function useIntegrations(query) {
    const { ...rest } = useElements('integrations', query);
    return {
        integrations: rest.items,
        ...rest,
    };
}

function useFieldMapping(selector) {
    const { item: fieldMapping, accessor, refresh, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.fieldMapping(selector) : undefined);
    async function apply(integrationKeys) {
        const result = await (accessor === null || accessor === void 0 ? void 0 : accessor.apply(integrationKeys));
        await refresh();
        return result !== null && result !== void 0 ? result : [];
    }
    async function reset() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.reset());
        await refresh();
    }
    return { fieldMapping, apply, reset, refresh, accessor, ...rest };
}

function useFieldMappingInstance(selector) {
    const { item: fieldMappingInstance, accessor, refresh, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.fieldMappingInstance(selector) : undefined);
    async function setup() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.setup());
        await refresh();
    }
    async function reset() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.reset());
        await refresh();
    }
    async function openConfiguration(options) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.openConfiguration(options);
    }
    return {
        fieldMappingInstance,
        accessor,
        refresh,
        setup,
        reset,
        openConfiguration,
        ...rest,
    };
}

function useFieldMappingInstances(query) {
    const { ...rest } = useElements('field-mapping-instances', query);
    return {
        fieldMappingInstances: rest.items,
        ...rest,
    };
}

function useFieldMappings(query) {
    const { ...rest } = useElements('field-mappings', query);
    return {
        fieldMappings: rest.items,
        ...rest,
    };
}

function useDataSource(selector) {
    const { item: dataSource, refresh, accessor, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.dataSource(selector) : undefined);
    async function apply(integrationKeys) {
        const result = await (accessor === null || accessor === void 0 ? void 0 : accessor.apply(integrationKeys));
        await refresh();
        return result !== null && result !== void 0 ? result : [];
    }
    async function reset() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.reset());
        await refresh();
    }
    return { dataSource, apply, reset, refresh, accessor, ...rest };
}

function useDataSourceInstance(selector) {
    const { item: dataSourceInstance, accessor, refresh, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.dataSourceInstance(selector) : undefined);
    async function setup() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.setup());
        await refresh();
    }
    async function reset() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.reset());
        await refresh();
    }
    async function openConfiguration(options) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.openConfiguration(options);
    }
    async function listRecords(request) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.listRecords(request);
    }
    async function findRecords(request) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.findRecords(request);
    }
    async function findRecordById(id) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.findRecordById(id);
    }
    async function createRecord(request) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.createRecord(request);
    }
    async function updateRecord(request) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.updateRecord(request);
    }
    async function deleteRecord(id) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.deleteRecord(id);
    }
    async function unifiedFieldsToNative(unifiedFields) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.unifiedFieldsToNative(unifiedFields);
    }
    async function getCollection() {
        return accessor === null || accessor === void 0 ? void 0 : accessor.getCollection();
    }
    return {
        dataSourceInstance,
        accessor,
        refresh,
        setup,
        reset,
        openConfiguration,
        listRecords,
        findRecords,
        findRecordById,
        createRecord,
        updateRecord,
        deleteRecord,
        unifiedFieldsToNative,
        getCollection,
        ...rest,
    };
}

function useDataSourceInstanceCollection(dataSourceInstance) {
    const integrationApp = useIntegrationApp();
    const { data, error, isLoading, mutate } = useSWR(dataSourceInstance ? `${dataSourceInstance.id}/collection` : null, () => integrationApp.dataSourceInstance(dataSourceInstance.id).getCollection());
    async function refresh() {
        return await mutate();
    }
    const collection = data;
    return {
        collection,
        refresh,
        error,
        loading: isLoading,
    };
}

function useDataSourceInstances(query) {
    const { ...rest } = useElements('data-source-instances', query);
    return {
        dataSourceInstances: rest.items,
        ...rest,
    };
}

function useDataSources(query) {
    const { ...rest } = useElements('data-sources', query);
    return {
        dataSources: rest.items,
        ...rest,
    };
}

function useAppEventSubscription(selector) {
    const { item: appEventSubscription, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.appEventSubscription(selector) : undefined);
    return { appEventSubscription, ...rest };
}

function useAppEventSubscriptions(query) {
    const { ...rest } = useElements('app-event-subscriptions', query);
    return {
        appEventSubscriptions: rest.items,
        ...rest,
    };
}

function useAppEventType(selector) {
    const { item: appEventType, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.appEventType(selector) : undefined);
    return { appEventType, ...rest };
}

function useAppEventTypes(query) {
    const { ...rest } = useElements('app-event-types', query);
    return {
        appEventTypes: rest.items,
        ...rest,
    };
}

function useAppEvents(query) {
    const { ...rest } = useElements('app-events', query);
    return {
        appEvents: rest.items,
        ...rest,
    };
}

function useAppDataSchema(selector) {
    const { item: appDataSchema, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.appDataSchema(selector) : undefined);
    return { appDataSchema, ...rest };
}

function useAppDataSchemas(query) {
    const { ...rest } = useElements('app-data-schemas', query);
    return {
        appDataSchemas: rest.items,
        ...rest,
    };
}

function useAppDataSchemaInstance(selector) {
    const { item: appDataSchemaInstance, accessor, refresh, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.appDataSchemaInstance(selector) : undefined);
    async function setup() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.setup());
        await refresh();
    }
    return {
        appDataSchemaInstance,
        accessor,
        refresh,
        setup,
        ...rest,
    };
}

function useAppDataSchemaInstances(query) {
    const { ...rest } = useElements('app-data-schema-instances', query);
    return {
        appDataSchemaInstances: rest.items,
        ...rest,
    };
}

function useFlow(selector) {
    const { item: flow, accessor, refresh, ...rest } = useElement(selector, (integrationApp) => (selector ? integrationApp.flow(selector) : undefined));
    async function apply(integrationKeys) {
        const result = await (accessor === null || accessor === void 0 ? void 0 : accessor.apply(integrationKeys));
        await refresh();
        return result;
    }
    async function reset() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.reset());
        return await refresh();
    }
    return { flow, apply, reset, refresh, accessor, ...rest };
}

function useFlows(query) {
    const { ...rest } = useElements('flows', query);
    return {
        flows: rest.items,
        ...rest,
    };
}

function useFlowInstance(selector) {
    const { item: flowInstance, accessor, refresh, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.flowInstance(selector) : undefined);
    async function enable() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.enable());
        await refresh();
    }
    async function disable() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.disable());
        await refresh();
    }
    async function reset(options) {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.reset(options));
        await refresh();
    }
    async function setup() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.setup());
        await refresh();
    }
    async function openConfiguration(options) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.openConfiguration(options);
    }
    async function run(options = {}) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.run(options);
    }
    async function startRun(options = {}) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.startRun(options);
    }
    return {
        flowInstance,
        accessor,
        refresh,
        enable,
        disable,
        reset,
        setup,
        openConfiguration,
        run,
        startRun,
        ...rest,
    };
}

function useFlowInstances(query) {
    const { ...rest } = useElements('flow-instances', query);
    return {
        flowInstances: rest.items,
        ...rest,
    };
}

function useFlowRun(id) {
    const { item: flowRun, archive, refresh, error, loading, } = useElement(id, (integrationApp) => id ? integrationApp.flowRun(id) : undefined);
    return {
        flowRun,
        error,
        loading,
        refresh,
        archive,
    };
}

function useFlowRuns(query) {
    const { ...rest } = useElements('flow-runs', query);
    return {
        flowRuns: rest.items,
        ...rest,
    };
}

function useDataLinkTable(selector) {
    const { item: dataLinkTable, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.dataLinkTable(selector) : undefined);
    return { dataLinkTable, ...rest };
}

function useDataLinkTableInstance(selector) {
    const { item: dataLinkTableInstance, accessor, refresh, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.dataLinkTableInstance(selector) : undefined);
    return {
        dataLinkTableInstance,
        accessor,
        refresh,
        findLinks: accessor === null || accessor === void 0 ? void 0 : accessor.findLinks,
        createLink: accessor === null || accessor === void 0 ? void 0 : accessor.createLink,
        deleteLink: accessor === null || accessor === void 0 ? void 0 : accessor.deleteLink,
        ...rest,
    };
}

function useDataLinkTableInstances(query) {
    const { ...rest } = useElements('data-link-table-instances', query);
    return {
        dataLinkTableInstances: rest.items,
        ...rest,
    };
}

function useDataLinkTables(query) {
    const { ...rest } = useElements('data-link-tables', query);
    return {
        dataLinkTables: rest.items,
        ...rest,
    };
}

function useAction(selector) {
    const { item: action, accessor, refresh, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.action(selector) : undefined);
    async function apply(integrationKeys) {
        const result = await (accessor === null || accessor === void 0 ? void 0 : accessor.apply(integrationKeys));
        await refresh();
        return result;
    }
    async function reset() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.reset());
        await refresh();
    }
    return { action, apply, reset, refresh, accessor, ...rest };
}

function useActionInstance(selector) {
    const { item: actionInstance, accessor, refresh, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.actionInstance(selector) : undefined);
    async function run(input) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.run(input);
    }
    async function setup() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.setup());
        await refresh();
    }
    async function reset() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.reset());
        await refresh();
    }
    async function openConfiguration(options) {
        return accessor === null || accessor === void 0 ? void 0 : accessor.open(options);
    }
    return {
        actionInstance,
        accessor,
        refresh,
        setup,
        reset,
        openConfiguration,
        run,
        ...rest,
    };
}

function useActionInstances(query) {
    const { ...rest } = useElements('action-instances', query);
    return {
        actionInstances: rest.items,
        ...rest,
    };
}

function useActions(query) {
    const { ...rest } = useElements('actions', query);
    return {
        actions: rest.items,
        ...rest,
    };
}

function useScreen(selector) {
    const { item: screen, ...rest } = useElement(selector, (integrationApp) => integrationApp.screen(selector));
    return { screen, ...rest };
}

function useCustomer(selector) {
    const { item: customer, ...rest } = useElement(selector, (c) => (selector ? c.customer(selector) : undefined));
    return { customer, ...rest };
}

function useCustomers(query) {
    const { ...rest } = useElements('customers', query);
    return {
        customers: rest.items,
        ...rest,
    };
}

function useScenario(selector) {
    const { item: scenario, ...rest } = useElement(selector, (integrationApp) => selector ? integrationApp.scenario(selector) : undefined);
    return { scenario, ...rest };
}

function useScenarios(query) {
    const { ...rest } = useElements('scenarios', query);
    return {
        scenarios: rest.items,
        ...rest,
    };
}

function useDataCollectionSpec({ path, key, integrationId, }) {
    var _a;
    const client = useIntegrationApp();
    const dataCollectionKey = key !== null && key !== void 0 ? key : (_a = parseDataLocationPath(path)) === null || _a === void 0 ? void 0 : _a.key;
    const { data: dataCollectionSpec } = useSWR(dataCollectionKey && integrationId
        ? `/integrations/${integrationId}/data/${dataCollectionKey}`
        : null, async () => {
        return client
            .integration(integrationId)
            .getDataCollection(dataCollectionKey);
    });
    return dataCollectionSpec;
}

function useExternalEventSubscriptions(query) {
    const { ...rest } = useElements('external-event-subscriptions', query);
    return {
        externalEventSubscriptions: rest.items,
        ...rest,
    };
}

function useExternalEventSubscription(id) {
    const { item: externalEventSubscription, accessor, refresh, ...rest } = useElement(id, (integrationApp) => id ? integrationApp.externalEventSubscription(id) : undefined);
    async function setup() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.setup());
        await refresh();
    }
    async function subscribe() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.subscribe());
        await refresh();
    }
    async function resubscribe() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.resubscribe());
        await refresh();
    }
    async function unsubscribe() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.unsubscribe());
        await refresh();
    }
    async function pullEvents() {
        await (accessor === null || accessor === void 0 ? void 0 : accessor.pullEvents());
        await refresh();
    }
    return {
        externalEventSubscription,
        accessor,
        refresh,
        setup,
        subscribe,
        resubscribe,
        unsubscribe,
        pullEvents,
        ...rest,
    };
}

function useIntegrationElement(accessor) {
    if (!accessor.getUniqueIdentifier) {
        throw new Error('This accessor can not be used for with `useIntegrationElement` hook. Method `getUniqueIdentifier` is missing.');
    }
    return useElement(accessor.getUniqueIdentifier(), () => accessor);
}

export { IntegrationAppProvider, useAction, useActionInstance, useActionInstances, useActions, useAppDataSchema, useAppDataSchemaInstance, useAppDataSchemaInstances, useAppDataSchemas, useAppEventSubscription, useAppEventSubscriptions, useAppEventType, useAppEventTypes, useAppEvents, useConnection, useConnections, useConnectorSpec, useCustomer, useCustomers, useDataCollectionSpec, useDataLinkTable, useDataLinkTableInstance, useDataLinkTableInstances, useDataLinkTables, useDataSource, useDataSourceInstance, useDataSourceInstanceCollection, useDataSourceInstances, useDataSources, useExternalEventSubscription, useExternalEventSubscriptions, useFieldMapping, useFieldMappingInstance, useFieldMappingInstances, useFieldMappings, useFlow, useFlowInstance, useFlowInstances, useFlowRun, useFlowRuns, useFlows, useIntegration, useIntegrationApp, useIntegrationAppSWR, useIntegrationElement, useIntegrations, useScenario, useScenarios, useScreen };
//# sourceMappingURL=index.module.mjs.map
